import i18next from 'i18next';

/**
 * LA MOLTEPLICITA': quanti eventi almeno deve avere il coupon perche' una
 * quota si giochi (decisioni del 2 novembre 2026).
 *
 * IL NUMERO NON SI INVENTA QUI. Arriva dal backoffice in due pezzi:
 *  - la base di Gestione Sport, uguale per tutte le skin, che il palinsesto
 *    mette su ogni partita e su ogni esito (`molteplicita`, con il livello da
 *    cui arriva in `molteplicitaDa`);
 *  - le regole MOLTEPLICITA dei Vincoli per la rete del giocatore, gia'
 *    risolte dal servizio, nel profilo di gioco (o nella schedina pubblica
 *    della skin per chi non e' collegato).
 * Qui si mettono insieme con la STESSA regola del servizio
 * (ticket-service, MolteplicitaValidation): vince il livello di catalogo piu'
 * preciso, sul suo livello la regola della rete prende il posto della base
 * (anche al ribasso: la deroga), e le regole mirate possono solo alzare.
 * Se un pezzo non arriva, si usa quello che c'e': a decidere davvero resta il
 * servizio, che rifiuta con la stessa frase.
 */

const NOMI = {
  1: 'singola', 2: 'doppia', 3: 'tripla', 4: 'quadrupla',
  5: 'quintupla', 6: 'sestupla', 7: 'settupla', 8: 'ottupla',
};

/** «tripla», «quadrupla»... sopra l'ottupla «multipla da 9». */
export function nomeMolteplicita(n) {
  return NOMI[n] ? i18next.t(NOMI[n]) : i18next.t('multipla da {{n}}', {n});
}

const LIVELLO = {EVENT: 4, COMPETITION: 3, COUNTRY: 2, SPORT: 1};

function livelloRegola(r) {
  if (r.eventoId) return 4;
  if (r.campionatoId) return 3;
  if (r.nazioneId) return 2;
  if (r.sportId) return 1;
  return 0;
}

function prende(r, p, marketKey, quota) {
  if (r.sportId && r.sportId !== p.sportId) return false;
  if (r.nazioneId && r.nazioneId !== p.countryId) return false;
  if (r.campionatoId && r.campionatoId !== p.competitionId) return false;
  if (r.eventoId && r.eventoId !== (p.eventId || p.id)) return false;
  if (r.marketKey && r.marketKey !== marketKey) return false;
  if (r.quotaDa != null || r.quotaA != null) {
    const q = Number(quota);
    if (!Number.isFinite(q)) return false;
    if (r.quotaDa != null && q < Number(r.quotaDa)) return false;
    if (r.quotaA != null && q > Number(r.quotaA)) return false;
  }
  return true;
}

/** Le regole di molteplicita' che arrivano col profilo, o con la schedina pubblica. */
export function regoleMolteplicita(profilo) {
  if (!profilo) return [];
  const daProfilo = profilo.mirati && profilo.mirati.MULTIPLA;
  const elenco = Array.isArray(daProfilo) ? daProfilo
    : (Array.isArray(profilo.molteplicita) ? profilo.molteplicita : []);
  return elenco.filter((r) => r && r.tipo === 'MOLTEPLICITA' && r.valore != null);
}

/**
 * La molteplicita' di una quota.
 *
 * @param partita  come arriva dal palinsesto, o l'evento della schedina: porta
 *                 sportId, countryId, competitionId, molteplicita, molteplicitaDa
 * @param marketKey il tipo di mercato con la soglia (OVER_UNDER@2.5), se si sa
 * @param quota    il prezzo, per le regole su una fascia di quota
 * @param regole   regoleMolteplicita(profilo)
 */
export function molteplicitaDi(partita, marketKey, quota, regole, esito) {
  if (!partita) return 1;
  const daEsito = esito && esito.molteplicita != null ? Number(esito.molteplicita) : null;
  const base = Math.max(1, daEsito || Number(partita.molteplicita) || 1);
  let valore = base;
  let livello = LIVELLO[partita.molteplicitaDa] || 0;
  let daRegola = false;

  (regole || []).forEach((r) => {
    if (r.soloAlza || !prende(r, partita, marketKey, quota)) return;
    const l = livelloRegola(r);
    const v = Number(r.valore);
    if (l > livello || (l === livello && (!daRegola || v > valore))) {
      valore = v; livello = l; daRegola = true;
    }
  });
  (regole || []).forEach((r) => {
    if (!r.soloAlza || !prende(r, partita, marketKey, quota)) return;
    valore = Math.max(valore, Number(r.valore));
  });
  return Math.max(1, valore);
}

/** Il colore del bordo per un livello, o null per la singola. */
export function coloreMolteplicita(n, colori) {
  if (!n || n <= 1 || !colori) return null;
  if (n >= 6) return colori['6+'] || null;
  return colori[String(n)] || null;
}

/** Lo stile del bordo di una quota: vuoto per la singola o senza colori. */
export function stileQuota(n, colori) {
  const c = coloreMolteplicita(n, colori);
  return c ? {boxShadow: `inset 0 0 0 2px ${c}`, position: 'relative'} : {};
}

/**
 * Che cosa manca alla schedina, o null se va bene.
 *
 * @param eventi   gli eventi della schedina, uno per partita
 * @param tipo     'singola' | 'multipla' | 'integrale' | 'sistema'
 * @param colonnaPiuPiccola sui sistemi la grandezza piu' piccola spuntata
 */
export function mancaMolteplicita(eventi, tipo, regole, colonnaPiuPiccola) {
  const elenco = eventi || [];
  if (elenco.length === 0) return null;
  let peggiore = null;
  elenco.forEach((e) => {
    const n = molteplicitaDi(e, null, null, regole);
    if (!peggiore || n > peggiore.n) peggiore = {n, evento: e};
  });
  if (!peggiore || peggiore.n <= 1) return null;

  const chi = peggiore.evento.competitionName || peggiore.evento.matchName || '';
  const nome = nomeMolteplicita(peggiore.n);
  if (tipo === 'sistema') {
    const k = Number(colonnaPiuPiccola) || 0;
    if (k === 0 || k >= peggiore.n) return null;
    return {
      molteplicita: peggiore.n,
      testo: i18next.t('{{chi}} si gioca almeno in {{nome}}: le colonne da {{k}} non bastano, alza il sistema a {{n}}/{{tot}} o togli {{evento}}',
          {chi, nome, k, n: peggiore.n, tot: elenco.length, evento: peggiore.evento.matchName || ''}),
    };
  }
  const mancano = peggiore.n - elenco.length;
  if (mancano <= 0) return null;
  return {
    molteplicita: peggiore.n,
    testo: i18next.t('{{chi}} si gioca almeno in {{nome}}: aggiungi {{quanti}}',
        {chi, nome, quanti: mancano === 1 ? i18next.t('1 evento') : i18next.t('{{n}} eventi', {n: mancano})}),
  };
}

// ---------------------------------------------------------------------------
// Il contesto: colori della skin e regole della rete, per chi disegna le quote
// ---------------------------------------------------------------------------

/* Lo tiene un posto solo, perche' chi disegna le quote (gli elenchi delle
   partite) e chi carica il profilo (la schedina) sono componenti lontani.
   Lo riempie profiloDiGioco ogni volta che legge il profilo o la schedina
   pubblica della skin: niente chiamate in piu'. */
let corrente = {colori: null, regole: []};
const ascoltatori = new Set();
let chiesto = false;

export function impostaContestoMolteplicita(profilo) {
  if (!profilo) return;
  corrente = {
    colori: profilo.coloriMolteplicita || corrente.colori,
    regole: regoleMolteplicita(profilo),
  };
  ascoltatori.forEach((f) => {
    try { f(); } catch (e) { /* un ascoltatore rotto non ferma gli altri */ }
  });
}

/** I colori e le regole di adesso. La prima volta chiede la schedina della skin. */
export function contestoMolteplicita() {
  if (!chiesto && !corrente.colori) {
    chiesto = true;
    import('./profiloDiGioco')
        .then((m) => m.caricaSchedinaSkin())
        .catch(() => null);
  }
  return corrente;
}

/** Ridisegna quando arrivano colori o regole nuove. Torna la funzione per smettere. */
export function ascoltaMolteplicita(f) {
  ascoltatori.add(f);
  return () => ascoltatori.delete(f);
}
